import {Hotkey} from '@wandb/components';
import * as globals from '@wandb/weave/common/css/globals.styles';
import {useBooleanState} from '@wandb/weave/common/util/hooks';
import {getTimeSegmentsInTimeZone} from '@wandb/weave/common/util/time';
import {
  IconBlogReport,
  IconDocumentation,
  IconHeadset,
  IconHelpAlt,
  IconKeyboard,
  IconLaptopLocalComputer,
  IconLightbulbInfo,
  IconListBullets,
  IconPrivacyOpen,
  IconShareWith,
  IconVideoPlay,
} from '@wandb/weave/components/Icon';
import {TooltipDeprecated} from '@wandb/weave/components/TooltipDeprecated';
import React, {
  type FC,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useLocation} from 'react-router';
import styled, {css} from 'styled-components';

import {envIsServerDeployment, urlPrefixed} from '../../../config';
import {
  type Organization,
  useOrganizationSubscriptionsBasicDetailsQuery,
} from '../../../generated/graphql';
import {UTM_Medium, UTM_Source, UTM_Term} from '../../../routes/paths';
import {
  trackNavResourcesMissingServerVersion,
  trackNavResourcesServerVersionFetchError,
  trackNavRightFeatureOpened,
} from '../../../services/analytics/productEvents';
import {useViewer} from '../../../state/viewer/hooks';
import {
  getPrimarySub,
  isNonAcademicEnterprise,
  isProPrimarySubscription,
  isTeamsPrimarySubscription,
} from '../../../util/accounts/pricing';
import {
  useIsAdminModeActiveAndIsNotLocalEnv,
  viewingAs,
} from '../../../util/admin';
import DOC_URLS from '../../../util/doc_urls';
import {useRampFlagShortcutDrawer} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {
  contactSalesDemo,
  courses,
  fullyConnected,
  homeQuickStart,
  marketingSite,
  pricing,
} from '../../../util/urls';
import {removeNullUndefinedOrFalse} from '../../../util/utility';
import BetaVersionModal, {betaVersion} from '../../BetaVersionModal';
import CopyableText from '../../CopyableText';
import {useKeyboardShortcutsDrawerStore} from '../../KeyboardShortcutsDrawer/keyboardShortcutsDrawerStore';
import {AccountSelectorContext} from './AccountSelectorContextProvider';
import NavMenu, {type NavMenuSection} from './NavMenu';
import {AccountType} from './types';

type NavResourcesProps = {};

const NavResourcesComp: FC<NavResourcesProps> = () => {
  const {
    state: menuOpen,
    setTrue: openMenu,
    setFalse: closeMenu,
  } = useBooleanState(false);
  const {
    state: tooltipOpen,
    setTrue: openTooltip,
    setFalse: closeTooltip,
  } = useBooleanState(false);
  const {
    state: betaModalOpen,
    setTrue: openBetaModal,
    setFalse: closeBetaModal,
  } = useBooleanState(false);

  const {selectedAccount} = useContext(AccountSelectorContext);
  const isAdminMode =
    useIsAdminModeActiveAndIsNotLocalEnv() || viewingAs() !== '';

  const handleOpen = useCallback(() => {
    trackNavRightFeatureOpened({
      feature: 'resources',
      selected_account: selectedAccount,
      is_admin_mode: isAdminMode,
    });
    openMenu();
  }, [selectedAccount, isAdminMode, openMenu]);

  const sections = useNavResourceMenuSections({
    openBetaModal,
  });

  return (
    <>
      <NavMenu
        open={menuOpen}
        onOpen={handleOpen}
        onClose={closeMenu}
        trigger={
          <Wrapper marginRight={8}>
            <TooltipDeprecated
              position="bottom center"
              open={tooltipOpen && !menuOpen}
              onOpen={openTooltip}
              onClose={closeTooltip}
              trigger={
                <Button
                  active={menuOpen}
                  showAdminBanner={isAdminMode}
                  aria-label="Resource & help">
                  <Icon />
                </Button>
              }>
              Resource & help
            </TooltipDeprecated>
          </Wrapper>
        }
        sections={sections}
      />
      {betaModalOpen && <BetaVersionModal onClose={closeBetaModal} />}
    </>
  );
};

const NavResources = memo(NavResourcesComp);

export default NavResources;

const SHOW_CLOUD_RESOURCES = !envIsServerDeployment;

const SUPPORT_START_TIME = 10;
const SUPPORT_END_TIME = 15;
const SUPPORT_DAYS = ['Tuesday', 'Wednesday', 'Thursday'];

type UseNavResourceMenuSectionsParams = {
  openBetaModal: () => void;
};

function useServerReleaseNotesUrl(): string {
  const [url, setUrl] = useState(DOC_URLS.serverReleaseNotes);

  // useEffect is appropriate here: this is a true one-time side effect (a
  // fire-and-forget fetch that runs once on mount to seed a URL). There is no
  // external store to subscribe to, no library that abstracts this away, and
  // the alternatives (module-level fetch + useSyncExternalStore) would add
  // significant complexity for no real benefit.
  useEffect(() => {
    if (!envIsServerDeployment) {
      return;
    }
    fetch(urlPrefixed('/api/server-version'))
      .then(res => res.text())
      .then(version => {
        const trimmed = version.trim();
        if (/^\d+\.\d+\.\d+$/.test(trimmed)) {
          setUrl(
            `${DOC_URLS.serverReleaseNotes}#v${trimmed.replace(/\./g, '-')}`
          );
        } else {
          trackNavResourcesMissingServerVersion({response: trimmed});
        }
      })
      .catch((err: unknown) => {
        trackNavResourcesServerVersionFetchError({
          error: err instanceof Error ? err.message : String(err),
        });
      });
  }, []);

  return url;
}

function useNavResourceMenuSections({
  openBetaModal,
}: UseNavResourceMenuSectionsParams): NavMenuSection[] {
  const viewer = useViewer();
  const location = useLocation();
  const serverReleaseNotesUrl = useServerReleaseNotesUrl();
  const shortcutDrawerEnabled = useRampFlagShortcutDrawer();
  const openShortcutsDrawer = useKeyboardShortcutsDrawerStore(
    state => state.open
  );

  const betaVersionSection: NavMenuSection | null = useMemo(() => {
    if (!betaVersion) {
      return null;
    }
    return {
      items: [
        {
          icon: <IconLaptopLocalComputer />,
          label: `Beta v. ${betaVersion}`,
          onClick: openBetaModal,
          rightSide: (
            <CopyContainer>
              <CopyableText
                text={''}
                copyText={`https://beta.wandb.ai${location.pathname}?betaVersion=${betaVersion}`}
                toastText="Copied beta link to clipboard."
                icon="link-alt"
              />
            </CopyContainer>
          ),
        },
      ],
    };
  }, [openBetaModal, location.pathname]);

  const resourcesLoggedOut: NavMenuSection[] = useMemo(
    () =>
      removeNullUndefinedOrFalse([
        {
          items: removeNullUndefinedOrFalse([
            {
              icon: <IconPrivacyOpen />,
              label: `Company website`,
              to: marketingSite(),
            },
          ]),
        },

        {
          items: removeNullUndefinedOrFalse([
            {
              icon: <IconDocumentation />,
              label: `Models documentation`,
              to: `${DOC_URLS.models}?utm_source=${UTM_Source.APP_RESOURCE_CENTER}&utm_medium=${UTM_Medium.APP}&utm_term=${UTM_Term.DOCS}`,
            },
            {
              icon: <IconDocumentation />,
              label: `Weave documentation`,
              to: DOC_URLS.weaveProduct,
            },
            {icon: <IconVideoPlay />, label: `W&B courses`, to: courses()},
            SHOW_CLOUD_RESOURCES && {
              icon: <IconBlogReport />,
              label: `Blog`,
              to: fullyConnected(),
            },
            envIsServerDeployment && {
              icon: <IconListBullets />,
              label: `Release notes`,
              to: serverReleaseNotesUrl,
            },
          ]),
        },

        SHOW_CLOUD_RESOURCES && {
          items: removeNullUndefinedOrFalse([
            {
              icon: <IconLaptopLocalComputer />,
              label: `Request a demo`,
              to: contactSalesDemo(),
            },
            {icon: <IconShareWith />, label: `Private teams`, to: pricing()},
          ]),
        },

        shortcutDrawerEnabled && {
          items: removeNullUndefinedOrFalse([
            {
              icon: <IconKeyboard />,
              label: `Keyboard shortcuts`,
              onClick: openShortcutsDrawer,
              rightSide: (
                <Hotkey name="shortcut-drawer" keys={['Primary', 'Slash']} />
              ),
            },
          ]),
        },

        betaVersionSection,
      ]),
    [
      betaVersionSection,
      serverReleaseNotesUrl,
      shortcutDrawerEnabled,
      openShortcutsDrawer,
    ]
  );

  const {selectedAccount} = useContext(AccountSelectorContext);
  const isPersonal = selectedAccount?.accountType === AccountType.Personal;
  const {data} = useOrganizationSubscriptionsBasicDetailsQuery({
    variables: {organizationId: selectedAccount?.id ?? ''},
    skip: !selectedAccount || isPersonal,
  });
  const org = (data?.organization as Organization) || null;
  const primarySubscription = getPrimarySub(org);

  // Replace with privilege that controls live support
  const isPaidSubscription =
    primarySubscription != null &&
    (isTeamsPrimarySubscription(primarySubscription) ||
      isProPrimarySubscription(primarySubscription) ||
      isNonAcademicEnterprise(primarySubscription));

  const currentTimePSTSegments = getTimeSegmentsInTimeZone(
    'America/Los_Angeles'
  );

  const isSupportDay = SUPPORT_DAYS.includes(currentTimePSTSegments.day);
  const isSupportWorkingHours =
    isSupportDay &&
    SUPPORT_START_TIME <= currentTimePSTSegments.hour &&
    currentTimePSTSegments.hour < SUPPORT_END_TIME;
  const showZendeskSupport = isPaidSubscription || isSupportWorkingHours;

  const resourcesLoggedIn: NavMenuSection[] = useMemo(
    () =>
      removeNullUndefinedOrFalse([
        {
          items: removeNullUndefinedOrFalse([
            {
              icon: <IconLightbulbInfo />,
              label: `Quickstart`,
              to: `${homeQuickStart()}?utm_source=${
                UTM_Source.APP_RESOURCE_CENTER
              }&utm_medium=${UTM_Medium.APP}&utm_term=${UTM_Term.QUICKSTART}`,
            },
            {
              icon: <IconDocumentation />,
              label: `Models documentation`,
              to: DOC_URLS.models,
            },
            {
              icon: <IconDocumentation />,
              label: `Weave documentation`,
              to: DOC_URLS.weaveProduct,
            },
            shortcutDrawerEnabled && {
              icon: <IconKeyboard />,
              label: `Keyboard shortcuts`,
              onClick: openShortcutsDrawer,
              rightSide: (
                <Hotkey name="shortcut-drawer" keys={['Primary', 'Slash']} />
              ),
            },

            showZendeskSupport && {
              icon: <IconHeadset />,
              label: `Contact support`,
              // Launches Zendesk widget
              onClick: () => (window as any).openWidget?.(),
            },
          ]),
        },

        {
          items: removeNullUndefinedOrFalse([
            {icon: <IconVideoPlay />, label: `W&B courses`, to: courses()},
            SHOW_CLOUD_RESOURCES && {
              icon: <IconBlogReport />,
              label: `Blog`,
              to: `${fullyConnected()}?utm_source=${
                UTM_Source.APP_RESOURCE_CENTER
              }&utm_medium=${UTM_Medium.APP}&utm_term=${
                UTM_Term.FULLY_CONNECTED
              }`,
            },
            envIsServerDeployment && {
              icon: <IconListBullets />,
              label: `Release notes`,
              to: serverReleaseNotesUrl,
            },
          ]),
        },

        betaVersionSection,
      ]),
    [
      showZendeskSupport,
      betaVersionSection,
      serverReleaseNotesUrl,
      shortcutDrawerEnabled,
      openShortcutsDrawer,
    ]
  );

  return viewer == null ? resourcesLoggedOut : resourcesLoggedIn;
}

export const Wrapper = styled.div<{marginRight?: number}>`
  margin-right: ${p => p.marginRight ?? 16}px;
  height: 100%;
  display: flex;
  align-items: center;
`;
Wrapper.displayName = 'S.Wrapper';

export const Button = styled.button<{
  active: boolean;
  showAdminBanner?: boolean;
}>`
  position: relative;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;

  color: ${props => (props.showAdminBanner ? globals.WHITE : globals.MOON_500)};
  opacity: ${props => (props.showAdminBanner ? 0.5 : 1)};
  &:hover {
    opacity: ${props => (props.showAdminBanner ? 0.8 : 1)};
    color: white;
  }

  background: transparent;
  &:hover {
    color: ${globals.WHITE};
    background: rgba(250, 250, 254, 0.12);
  }
  ${p =>
    p.active &&
    css`
      &&& {
        color: ${globals.WHITE};
        background: rgba(250, 250, 254, 0.16);
      }
    `}

  &:focus-visible {
    outline: 2px solid rgba(0, 150, 173, 0.6);
  }
`;
Button.displayName = 'S.Button';

const Icon = styled(IconHelpAlt)`
  display: block;
  width: 24px;
  height: 24px;
`;
Icon.displayName = 'S.Icon';

const CopyContainer = styled.div`
  &&&&& {
    .copyable-text {
      margin-left: 8px;
      padding-left: 4px;
      i.icon.copy-icon {
        opacity: 1;
        font-size: 1em;
      }
    }
  }
`;
CopyContainer.displayName = 'S.CopyContainer';
